'use client';

import { getToolAuthToken } from '@/lib/browser-tool-usage';
import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
} from 'react';

type OutputFormat = 'png' | 'jpeg' | 'webp';
type FramingMode = 'crop' | 'fit' | 'fill';
type BackgroundPreset = 'white' | 'light' | 'warm';
type WatermarkPosition = 'bottom-right' | 'bottom-left' | 'top-right' | 'top-left' | 'center';
type WatermarkKind = 'text' | 'logo';
type ProductImagePrepVariant = {
  lockedPresetId?: string;
  defaultWorkflowId?: string;
  defaultProductPresetId?: string;
  defaultProductFramingMode?: FramingMode;
  defaultProductBackgroundPreset?: BackgroundPreset;
  defaultProductOutputFormat?: OutputFormat;
  defaultProductQuality?: number;
  defaultProductWatermarkEnabled?: boolean;
  defaultProductWatermarkKind?: WatermarkKind;
  defaultProductWatermarkText?: string;
  defaultProductWatermarkPosition?: WatermarkPosition;
  defaultProductWatermarkScale?: number;
  defaultProductWatermarkOpacity?: number;
  defaultProductWatermarkPadding?: number;
  hidePresetChooser?: boolean;
  badgeLabel?: string;
  toolSlug?: string;
};

type ListingPreset = {
  id: string;
  label: string;
  width: number;
  height: number;
  hint: string;
  safeInset: number;
};

type WorkflowPreset = {
  id: string;
  label: string;
  summary: string;
  presetId: string;
  framingMode: FramingMode;
  backgroundPreset: BackgroundPreset;
  outputFormat: OutputFormat;
  quality: number;
  watermarkEnabled: boolean;
  watermarkKind: WatermarkKind;
  watermarkText: string;
  watermarkPosition: WatermarkPosition;
  watermarkScale: number;
  watermarkOpacity: number;
  watermarkPadding: number;
};

type LoadedImage = {
  element: HTMLImageElement;
  name: string;
  type: string;
  size: number;
  width: number;
  height: number;
  zoom: number;
  offsetX: number;
  offsetY: number;
};

type WatermarkLogo = {
  element: HTMLImageElement;
  url: string;
  name: string;
};

const listingPresets: ListingPreset[] = [
  {
    id: 'marketplace-square',
    label: 'Marketplace square',
    width: 1600,
    height: 1600,
    hint: 'Square canvas for marketplace cards, storefront grids, and primary listing images.',
    safeInset: 0.1,
  },
  {
    id: 'product-portrait',
    label: 'Product portrait',
    width: 1200,
    height: 1500,
    hint: 'Taller canvas for apparel, beauty, packaging, and portrait-heavy catalog images.',
    safeInset: 0.11,
  },
  {
    id: 'lightweight-thumbnail',
    label: 'Lightweight thumbnail',
    width: 1200,
    height: 1200,
    hint: 'Lighter square export for faster uploads, product feeds, and support-friendly thumbnails.',
    safeInset: 0.11,
  },
  {
    id: 'catalog-wide',
    label: 'Catalog wide',
    width: 1600,
    height: 900,
    hint: 'Wide canvas for collection cards, hero rails, and storefront modules that crop near the edges.',
    safeInset: 0.08,
  },
];

const workflowPresets: WorkflowPreset[] = [
  {
    id: 'marketplace-square',
    label: 'Marketplace square',
    summary: 'Clean square setup for marketplace and storefront cards.',
    presetId: 'marketplace-square',
    framingMode: 'crop',
    backgroundPreset: 'white',
    outputFormat: 'jpeg',
    quality: 0.84,
    watermarkEnabled: false,
    watermarkKind: 'text',
    watermarkText: 'Brand',
    watermarkPosition: 'bottom-right',
    watermarkScale: 0.16,
    watermarkOpacity: 0.7,
    watermarkPadding: 0.05,
  },
  {
    id: 'product-portrait',
    label: 'Product portrait',
    summary: 'Taller frame for fashion, beauty, accessories, and packaging.',
    presetId: 'product-portrait',
    framingMode: 'crop',
    backgroundPreset: 'white',
    outputFormat: 'jpeg',
    quality: 0.88,
    watermarkEnabled: false,
    watermarkKind: 'text',
    watermarkText: 'Brand',
    watermarkPosition: 'bottom-right',
    watermarkScale: 0.16,
    watermarkOpacity: 0.7,
    watermarkPadding: 0.05,
  },
  {
    id: 'lightweight-thumbnail',
    label: 'Lightweight thumbnail',
    summary: 'Smaller WebP-ready setup for lighter upload weight.',
    presetId: 'lightweight-thumbnail',
    framingMode: 'fit',
    backgroundPreset: 'white',
    outputFormat: 'webp',
    quality: 0.72,
    watermarkEnabled: false,
    watermarkKind: 'text',
    watermarkText: 'Brand',
    watermarkPosition: 'bottom-right',
    watermarkScale: 0.15,
    watermarkOpacity: 0.66,
    watermarkPadding: 0.05,
  },
  {
    id: 'branded-image',
    label: 'Branded image',
    summary: 'Product card with a gentle brand mark and warmer canvas tone.',
    presetId: 'marketplace-square',
    framingMode: 'fill',
    backgroundPreset: 'warm',
    outputFormat: 'png',
    quality: 0.92,
    watermarkEnabled: true,
    watermarkKind: 'text',
    watermarkText: 'Brand',
    watermarkPosition: 'bottom-right',
    watermarkScale: 0.16,
    watermarkOpacity: 0.72,
    watermarkPadding: 0.05,
  },
];

function getWorkflowById(workflowId?: string | null) {
  return workflowId
    ? (workflowPresets.find((workflow) => workflow.id === workflowId) ?? null)
    : null;
}

const framingModes: Array<{
  value: FramingMode;
  label: string;
  hint: string;
}> = [
  {
    value: 'crop',
    label: 'Fill frame',
    hint: 'Covers the full canvas. Best when a small crop is acceptable.',
  },
  {
    value: 'fit',
    label: 'Keep full product',
    hint: 'Keeps the full product visible inside the listing canvas.',
  },
  {
    value: 'fill',
    label: 'Keep full + background',
    hint: 'Keeps the full product visible and fills spare canvas space with the selected tone.',
  },
];

const outputOptions: Array<{
  value: OutputFormat;
  label: string;
  mimeType: string;
  extension: string;
  qualityEnabled: boolean;
}> = [
  { value: 'png', label: 'PNG', mimeType: 'image/png', extension: 'png', qualityEnabled: false },
  { value: 'jpeg', label: 'JPG', mimeType: 'image/jpeg', extension: 'jpg', qualityEnabled: true },
  { value: 'webp', label: 'WebP', mimeType: 'image/webp', extension: 'webp', qualityEnabled: true },
];

const backgroundOptions: Array<{
  value: BackgroundPreset;
  label: string;
  color: string;
}> = [
  { value: 'white', label: 'White canvas', color: '#ffffff' },
  { value: 'light', label: 'Soft gray canvas', color: '#eef1f5' },
  { value: 'warm', label: 'Warm canvas', color: '#f5efe5' },
];

const watermarkPositions: Array<{
  value: WatermarkPosition;
  label: string;
}> = [
  { value: 'bottom-right', label: 'Bottom right' },
  { value: 'bottom-left', label: 'Bottom left' },
  { value: 'top-right', label: 'Top right' },
  { value: 'top-left', label: 'Top left' },
  { value: 'center', label: 'Center' },
];

const supportedTypes = new Set(['image/png', 'image/jpeg', 'image/webp', 'image/gif', 'image/bmp']);
const maxFileSize = 12 * 1024 * 1024;

function clamp(value: number, min: number, max: number) {
  return Math.min(Math.max(value, min), max);
}

function formatFileSize(size: number) {
  if (size < 1024) {
    return `${size} B`;
  }

  if (size < 1024 * 1024) {
    return `${(size / 1024).toFixed(1)} KB`;
  }

  return `${(size / (1024 * 1024)).toFixed(2)} MB`;
}

function getBaseName(fileName: string) {
  const lastDot = fileName.lastIndexOf('.');
  return lastDot > 0 ? fileName.slice(0, lastDot) : fileName;
}

function normalizeSourceLabel(type: string | null) {
  if (!type) {
    return 'Auto';
  }

  const label = type.replace('image/', '').toUpperCase();
  return label === 'JPEG' ? 'JPG' : label;
}

function getContainSize(width: number, height: number, maxWidth: number, maxHeight: number) {
  const scale = Math.min(maxWidth / width, maxHeight / height, 1);

  return {
    width: Math.max(1, Math.round(width * scale)),
    height: Math.max(1, Math.round(height * scale)),
  };
}

async function loadImageFromUrl(url: string) {
  return await new Promise<HTMLImageElement>((resolve, reject) => {
    const nextImage = new Image();
    nextImage.onload = () => resolve(nextImage);
    nextImage.onerror = () =>
      reject(new Error('This image format is not supported in your browser yet.'));
    nextImage.src = url;
  });
}

function triggerDownload(url: string, name: string) {
  const link = document.createElement('a');
  link.href = url;
  link.download = name;
  document.body.appendChild(link);
  link.click();
  link.remove();
}

function getPlacementMetrics(
  imageWidth: number,
  imageHeight: number,
  targetWidth: number,
  targetHeight: number,
  mode: FramingMode,
  zoom: number,
) {
  const scaleBase =
    mode === 'crop'
      ? Math.max(targetWidth / imageWidth, targetHeight / imageHeight)
      : Math.min(targetWidth / imageWidth, targetHeight / imageHeight);
  const scale = scaleBase * zoom;
  const renderedWidth = imageWidth * scale;
  const renderedHeight = imageHeight * scale;
  const baseX = (targetWidth - renderedWidth) / 2;
  const baseY = (targetHeight - renderedHeight) / 2;
  const minX = renderedWidth > targetWidth ? targetWidth - renderedWidth : 0;
  const maxX = renderedWidth > targetWidth ? 0 : targetWidth - renderedWidth;
  const minY = renderedHeight > targetHeight ? targetHeight - renderedHeight : 0;
  const maxY = renderedHeight > targetHeight ? 0 : targetHeight - renderedHeight;

  return {
    renderedWidth,
    renderedHeight,
    baseX,
    baseY,
    minX,
    maxX,
    minY,
    maxY,
  };
}

function clampOffsets(
  imageWidth: number,
  imageHeight: number,
  targetWidth: number,
  targetHeight: number,
  mode: FramingMode,
  zoom: number,
  offsetX: number,
  offsetY: number,
) {
  const metrics = getPlacementMetrics(
    imageWidth,
    imageHeight,
    targetWidth,
    targetHeight,
    mode,
    zoom,
  );

  return {
    x: clamp(offsetX, metrics.minX - metrics.baseX, metrics.maxX - metrics.baseX),
    y: clamp(offsetY, metrics.minY - metrics.baseY, metrics.maxY - metrics.baseY),
  };
}

function getDefaultPlacement(
  imageWidth: number,
  imageHeight: number,
  preset: ListingPreset,
  mode: FramingMode,
) {
  const imageAspect = imageWidth / imageHeight;
  const targetAspect = preset.width / preset.height;

  if (mode === 'fit') {
    return { zoom: 1, offsetX: 0, offsetY: 0 };
  }

  if (mode === 'fill') {
    return { zoom: imageAspect < targetAspect ? 0.98 : 1, offsetX: 0, offsetY: 0 };
  }

  return {
    zoom: imageAspect < targetAspect - 0.2 ? 1.04 : 1,
    offsetX: 0,
    offsetY: imageAspect < targetAspect ? -Math.min(preset.height * 0.06, 96) : 0,
  };
}

function getBackgroundConfig(preset: BackgroundPreset) {
  return backgroundOptions.find((option) => option.value === preset) ?? backgroundOptions[0];
}

function getQualityProfile(format: OutputFormat, quality: number) {
  if (format === 'png') {
    return 'Clean edges';
  }

  if (quality >= 0.9) {
    return 'Cleaner detail';
  }

  if (quality >= 0.78) {
    return 'Balanced weight';
  }

  return 'Lighter upload';
}

function getEstimatedOutputWeight(
  image: LoadedImage | null,
  format: OutputFormat,
  quality: number,
) {
  if (!image) {
    return 'Will appear after upload';
  }

  if (format === 'png') {
    return `Around ${formatFileSize(Math.round(image.size * 0.9))}`;
  }

  const ratio = format === 'webp' ? quality * 0.42 : quality * 0.58;
  return `Around ${formatFileSize(Math.max(45_000, Math.round(image.size * ratio)))}`;
}

function drawTextWatermark(
  context: CanvasRenderingContext2D,
  canvasWidth: number,
  canvasHeight: number,
  text: string,
  opacity: number,
  position: WatermarkPosition,
  scale: number,
  paddingRatio: number,
) {
  const cleanText = text.trim();

  if (!cleanText) {
    return;
  }

  const minDimension = Math.min(canvasWidth, canvasHeight);
  const fontSize = Math.max(18, Math.round(minDimension * scale * 0.18));
  const padding = Math.max(18, Math.round(minDimension * paddingRatio));

  context.save();
  context.globalAlpha = opacity;
  context.font = `700 ${fontSize}px ui-sans-serif, system-ui, sans-serif`;
  context.textBaseline = position.startsWith('bottom')
    ? 'bottom'
    : position.startsWith('top')
      ? 'top'
      : 'middle';
  context.textAlign =
    position === 'bottom-right' || position === 'top-right'
      ? 'right'
      : position === 'bottom-left' || position === 'top-left'
        ? 'left'
        : 'center';

  let x = canvasWidth / 2;
  let y = canvasHeight / 2;

  if (position === 'bottom-right') {
    x = canvasWidth - padding;
    y = canvasHeight - padding;
  } else if (position === 'bottom-left') {
    x = padding;
    y = canvasHeight - padding;
  } else if (position === 'top-right') {
    x = canvasWidth - padding;
    y = padding;
  } else if (position === 'top-left') {
    x = padding;
    y = padding;
  }

  context.strokeStyle = 'rgba(15, 23, 42, 0.36)';
  context.lineWidth = Math.max(2, Math.round(fontSize * 0.12));
  context.lineJoin = 'round';
  context.strokeText(cleanText, x, y);
  context.fillStyle = 'rgba(255, 255, 255, 0.95)';
  context.fillText(cleanText, x, y);
  context.restore();
}

function drawLogoWatermark(
  context: CanvasRenderingContext2D,
  canvasWidth: number,
  canvasHeight: number,
  logo: HTMLImageElement,
  opacity: number,
  position: WatermarkPosition,
  scale: number,
  paddingRatio: number,
) {
  const minDimension = Math.min(canvasWidth, canvasHeight);
  const padding = Math.max(18, Math.round(minDimension * paddingRatio));
  const targetWidth = Math.max(64, Math.round(minDimension * scale));
  const aspect = logo.naturalHeight ? logo.naturalWidth / logo.naturalHeight : 1;
  const targetHeight = Math.max(28, Math.round(targetWidth / Math.max(aspect, 0.2)));

  let x = (canvasWidth - targetWidth) / 2;
  let y = (canvasHeight - targetHeight) / 2;

  if (position === 'bottom-right') {
    x = canvasWidth - targetWidth - padding;
    y = canvasHeight - targetHeight - padding;
  } else if (position === 'bottom-left') {
    x = padding;
    y = canvasHeight - targetHeight - padding;
  } else if (position === 'top-right') {
    x = canvasWidth - targetWidth - padding;
    y = padding;
  } else if (position === 'top-left') {
    x = padding;
    y = padding;
  }

  context.save();
  context.globalAlpha = opacity;
  context.drawImage(logo, x, y, targetWidth, targetHeight);
  context.restore();
}

function drawPreparedImage(
  context: CanvasRenderingContext2D,
  image: LoadedImage,
  preset: ListingPreset,
  referenceWidth: number,
  referenceHeight: number,
  mode: FramingMode,
  backgroundColor: string,
  outputFormat: OutputFormat,
  watermarkEnabled: boolean,
  watermarkKind: WatermarkKind,
  watermarkText: string,
  watermarkLogo: WatermarkLogo | null,
  watermarkScale: number,
  watermarkOpacity: number,
  watermarkPadding: number,
  watermarkPosition: WatermarkPosition,
) {
  context.clearRect(0, 0, preset.width, preset.height);

  if (mode === 'fill' || mode === 'fit' || outputFormat === 'jpeg') {
    context.save();
    context.fillStyle = backgroundColor;
    context.fillRect(0, 0, preset.width, preset.height);
    context.restore();
  }

  const scaledOffsetX = image.offsetX * (preset.width / referenceWidth);
  const scaledOffsetY = image.offsetY * (preset.height / referenceHeight);
  const metrics = getPlacementMetrics(
    image.width,
    image.height,
    preset.width,
    preset.height,
    mode,
    image.zoom,
  );
  const clampedOffsets = clampOffsets(
    image.width,
    image.height,
    preset.width,
    preset.height,
    mode,
    image.zoom,
    scaledOffsetX,
    scaledOffsetY,
  );
  const drawX = metrics.baseX + clampedOffsets.x;
  const drawY = metrics.baseY + clampedOffsets.y;

  context.drawImage(image.element, drawX, drawY, metrics.renderedWidth, metrics.renderedHeight);

  if (!watermarkEnabled) {
    return;
  }

  if (watermarkKind === 'logo' && watermarkLogo?.element) {
    drawLogoWatermark(
      context,
      preset.width,
      preset.height,
      watermarkLogo.element,
      watermarkOpacity,
      watermarkPosition,
      watermarkScale,
      watermarkPadding,
    );
    return;
  }

  drawTextWatermark(
    context,
    preset.width,
    preset.height,
    watermarkText,
    watermarkOpacity,
    watermarkPosition,
    watermarkScale,
    watermarkPadding,
  );
}

type BrowserProductImagePrepProps = {
  variant?: ProductImagePrepVariant;
};

export default function BrowserProductImagePrep({ variant }: BrowserProductImagePrepProps) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const logoInputRef = useRef<HTMLInputElement | null>(null);
  const previewFrameRef = useRef<HTMLDivElement | null>(null);
  const previewCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const pointerDragRef = useRef<{
    pointerId: number;
    startX: number;
    startY: number;
    offsetX: number;
    offsetY: number;
  } | null>(null);
  const lockedPreset = useMemo(
    () =>
      variant?.lockedPresetId
        ? (listingPresets.find((preset) => preset.id === variant.lockedPresetId) ?? null)
        : null,
    [variant?.lockedPresetId],
  );
  const visiblePresets = useMemo(
    () => (lockedPreset ? [lockedPreset] : listingPresets),
    [lockedPreset],
  );
  const defaultWorkflow = useMemo(
    () => getWorkflowById(variant?.defaultWorkflowId) ?? workflowPresets[0],
    [variant?.defaultWorkflowId],
  );
  const hasVariantManualOverrides = Boolean(
    variant?.defaultProductPresetId ||
    variant?.defaultProductFramingMode ||
    variant?.defaultProductBackgroundPreset ||
    variant?.defaultProductOutputFormat ||
    variant?.defaultProductQuality !== undefined ||
    variant?.defaultProductWatermarkEnabled !== undefined ||
    variant?.defaultProductWatermarkKind ||
    variant?.defaultProductWatermarkText ||
    variant?.defaultProductWatermarkPosition ||
    variant?.defaultProductWatermarkScale !== undefined ||
    variant?.defaultProductWatermarkOpacity !== undefined ||
    variant?.defaultProductWatermarkPadding !== undefined,
  );
  const defaultPreset = useMemo(
    () =>
      lockedPreset ??
      (variant?.defaultProductPresetId
        ? (listingPresets.find((preset) => preset.id === variant.defaultProductPresetId) ?? null)
        : null) ??
      listingPresets.find((preset) => preset.id === defaultWorkflow.presetId) ??
      listingPresets[0],
    [defaultWorkflow.presetId, lockedPreset, variant?.defaultProductPresetId],
  );
  const [loadedImage, setLoadedImage] = useState<LoadedImage | null>(null);
  const [activeWorkflowId, setActiveWorkflowId] = useState(
    hasVariantManualOverrides ? '' : defaultWorkflow.id,
  );
  const [activePresetId, setActivePresetId] = useState(defaultPreset.id);
  const [framingMode, setFramingMode] = useState<FramingMode>(
    variant?.defaultProductFramingMode ?? defaultWorkflow.framingMode,
  );
  const [outputFormat, setOutputFormat] = useState<OutputFormat>(
    variant?.defaultProductOutputFormat ?? defaultWorkflow.outputFormat,
  );
  const [quality, setQuality] = useState(variant?.defaultProductQuality ?? defaultWorkflow.quality);
  const [backgroundPreset, setBackgroundPreset] = useState<BackgroundPreset>(
    variant?.defaultProductBackgroundPreset ?? defaultWorkflow.backgroundPreset,
  );
  const [watermarkEnabled, setWatermarkEnabled] = useState(
    variant?.defaultProductWatermarkEnabled ?? defaultWorkflow.watermarkEnabled,
  );
  const [watermarkKind, setWatermarkKind] = useState<WatermarkKind>(
    variant?.defaultProductWatermarkKind ?? defaultWorkflow.watermarkKind,
  );
  const [watermarkText, setWatermarkText] = useState(
    variant?.defaultProductWatermarkText ?? defaultWorkflow.watermarkText,
  );
  const [watermarkLogo, setWatermarkLogo] = useState<WatermarkLogo | null>(null);
  const [watermarkScale, setWatermarkScale] = useState(
    variant?.defaultProductWatermarkScale ?? defaultWorkflow.watermarkScale,
  );
  const [watermarkOpacity, setWatermarkOpacity] = useState(
    variant?.defaultProductWatermarkOpacity ?? defaultWorkflow.watermarkOpacity,
  );
  const [watermarkPadding, setWatermarkPadding] = useState(
    variant?.defaultProductWatermarkPadding ?? defaultWorkflow.watermarkPadding,
  );
  const [watermarkPosition, setWatermarkPosition] = useState<WatermarkPosition>(
    variant?.defaultProductWatermarkPosition ?? defaultWorkflow.watermarkPosition,
  );
  const [isAdvancedOpen, setIsAdvancedOpen] = useState(false);
  const [isDragActive, setIsDragActive] = useState(false);
  const [isPointerDragging, setIsPointerDragging] = useState(false);
  const [isLoadingImage, setIsLoadingImage] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [creditMessage, setCreditMessage] = useState<string | null>(null);
  const [resultSize, setResultSize] = useState<number | null>(null);
  const [previewFrameSize, setPreviewFrameSize] = useState<{
    width: number;
    height: number;
  } | null>(null);

  const activePreset = useMemo(
    () => visiblePresets.find((preset) => preset.id === activePresetId) ?? visiblePresets[0],
    [activePresetId, visiblePresets],
  );
  const activeWorkflow = useMemo(
    () => workflowPresets.find((preset) => preset.id === activeWorkflowId) ?? null,
    [activeWorkflowId],
  );
  const selectedOutput = useMemo(
    () => outputOptions.find((option) => option.value === outputFormat) ?? outputOptions[1],
    [outputFormat],
  );
  const activeFramingMode = useMemo(
    () => framingModes.find((mode) => mode.value === framingMode) ?? framingModes[0],
    [framingMode],
  );
  const activeBackground = useMemo(() => getBackgroundConfig(backgroundPreset), [backgroundPreset]);
  const previewSize = useMemo(
    () =>
      getContainSize(
        activePreset.width,
        activePreset.height,
        Math.max(320, (previewFrameSize?.width ?? 1120) - 40),
        Math.max(260, (previewFrameSize?.height ?? 760) - 40),
      ),
    [activePreset.height, activePreset.width, previewFrameSize],
  );
  const safeInsetLabel = `${Math.round(activePreset.safeInset * 100)}%`;
  const qualityProfile = useMemo(
    () => getQualityProfile(outputFormat, quality),
    [outputFormat, quality],
  );
  const estimatedOutputWeight = useMemo(
    () => getEstimatedOutputWeight(loadedImage, outputFormat, quality),
    [loadedImage, outputFormat, quality],
  );

  useEffect(() => {
    return () => {
      if (watermarkLogo?.url) {
        URL.revokeObjectURL(watermarkLogo.url);
      }
    };
  }, [watermarkLogo?.url]);

  useEffect(() => {
    if (!previewFrameRef.current) {
      return;
    }

    const frame = previewFrameRef.current;
    const syncFrameSize = () => {
      setPreviewFrameSize({
        width: frame.clientWidth,
        height: frame.clientHeight,
      });
    };

    syncFrameSize();

    const observer = new ResizeObserver(() => syncFrameSize());
    observer.observe(frame);

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const nextPreset = defaultPreset;
    const nextFramingMode = variant?.defaultProductFramingMode ?? defaultWorkflow.framingMode;

    setActiveWorkflowId(hasVariantManualOverrides ? '' : defaultWorkflow.id);
    setActivePresetId(nextPreset.id);
    setFramingMode(nextFramingMode);
    setOutputFormat(variant?.defaultProductOutputFormat ?? defaultWorkflow.outputFormat);
    setQuality(variant?.defaultProductQuality ?? defaultWorkflow.quality);
    setBackgroundPreset(
      variant?.defaultProductBackgroundPreset ?? defaultWorkflow.backgroundPreset,
    );
    setWatermarkEnabled(
      variant?.defaultProductWatermarkEnabled ?? defaultWorkflow.watermarkEnabled,
    );
    setWatermarkKind(variant?.defaultProductWatermarkKind ?? defaultWorkflow.watermarkKind);
    setWatermarkText(variant?.defaultProductWatermarkText ?? defaultWorkflow.watermarkText);
    setWatermarkScale(variant?.defaultProductWatermarkScale ?? defaultWorkflow.watermarkScale);
    setWatermarkOpacity(
      variant?.defaultProductWatermarkOpacity ?? defaultWorkflow.watermarkOpacity,
    );
    setWatermarkPadding(
      variant?.defaultProductWatermarkPadding ?? defaultWorkflow.watermarkPadding,
    );
    setWatermarkPosition(
      variant?.defaultProductWatermarkPosition ?? defaultWorkflow.watermarkPosition,
    );
    setIsAdvancedOpen(false);

    setLoadedImage((current) => {
      if (!current) {
        return current;
      }

      const placement = getDefaultPlacement(
        current.width,
        current.height,
        nextPreset,
        nextFramingMode,
      );

      return {
        ...current,
        zoom: placement.zoom,
        offsetX: placement.offsetX,
        offsetY: placement.offsetY,
      };
    });
  }, [
    defaultPreset,
    defaultWorkflow,
    hasVariantManualOverrides,
    variant?.defaultProductBackgroundPreset,
    variant?.defaultProductFramingMode,
    variant?.defaultProductOutputFormat,
    variant?.defaultProductQuality,
    variant?.defaultProductWatermarkEnabled,
    variant?.defaultProductWatermarkKind,
    variant?.defaultProductWatermarkOpacity,
    variant?.defaultProductWatermarkPadding,
    variant?.defaultProductWatermarkPosition,
    variant?.defaultProductWatermarkScale,
    variant?.defaultProductWatermarkText,
  ]);

  useEffect(() => {
    if (!loadedImage || !previewCanvasRef.current) {
      return;
    }

    const canvas = previewCanvasRef.current;
    const devicePixelRatio = window.devicePixelRatio || 1;
    canvas.width = Math.round(previewSize.width * devicePixelRatio);
    canvas.height = Math.round(previewSize.height * devicePixelRatio);

    const context = canvas.getContext('2d');

    if (!context) {
      return;
    }

    context.setTransform(1, 0, 0, 1, 0, 0);
    context.scale(devicePixelRatio, devicePixelRatio);
    drawPreparedImage(
      context,
      loadedImage,
      { ...activePreset, width: previewSize.width, height: previewSize.height },
      activePreset.width,
      activePreset.height,
      framingMode,
      activeBackground.color,
      outputFormat,
      watermarkEnabled,
      watermarkKind,
      watermarkText,
      watermarkLogo,
      watermarkScale,
      watermarkOpacity,
      watermarkPadding,
      watermarkPosition,
    );
  }, [
    activeBackground.color,
    activePreset,
    framingMode,
    loadedImage,
    outputFormat,
    previewSize.height,
    previewSize.width,
    watermarkEnabled,
    watermarkKind,
    watermarkLogo,
    watermarkOpacity,
    watermarkPadding,
    watermarkPosition,
    watermarkScale,
    watermarkText,
  ]);

  function resetOutputState() {
    setResultSize(null);
    setCreditMessage(null);
  }

  function markWorkflowCustom() {
    setActiveWorkflowId('');
  }

  function applyWorkflowPreset(workflow: WorkflowPreset) {
    setActiveWorkflowId(workflow.id);
    if (!lockedPreset) {
      setActivePresetId(workflow.presetId);
    }
    setFramingMode(workflow.framingMode);
    setOutputFormat(workflow.outputFormat);
    setQuality(workflow.quality);
    setBackgroundPreset(workflow.backgroundPreset);
    setWatermarkEnabled(workflow.watermarkEnabled);
    setWatermarkKind(workflow.watermarkKind);
    setWatermarkText(workflow.watermarkText);
    setWatermarkPosition(workflow.watermarkPosition);
    setWatermarkScale(workflow.watermarkScale);
    setWatermarkOpacity(workflow.watermarkOpacity);
    setWatermarkPadding(workflow.watermarkPadding);
    resetOutputState();

    if (loadedImage) {
      const placement = getDefaultPlacement(
        loadedImage.width,
        loadedImage.height,
        lockedPreset ??
          listingPresets.find((preset) => preset.id === workflow.presetId) ??
          activePreset,
        workflow.framingMode,
      );
      setLoadedImage((current) =>
        current
          ? {
              ...current,
              zoom: placement.zoom,
              offsetX: placement.offsetX,
              offsetY: placement.offsetY,
            }
          : current,
      );
    }
  }

  async function handleFileSelection(file: File | null) {
    setErrorMessage(null);
    setCreditMessage(null);
    setResultSize(null);

    if (!file) {
      setLoadedImage(null);
      return;
    }

    if (!supportedTypes.has(file.type) || file.size > maxFileSize) {
      setErrorMessage('Choose one PNG, JPG, JPEG, WebP, GIF, or BMP image up to 12 MB.');
      return;
    }

    setIsLoadingImage(true);

    const sourceUrl = URL.createObjectURL(file);

    try {
      const image = await loadImageFromUrl(sourceUrl);
      const placement = getDefaultPlacement(
        image.naturalWidth,
        image.naturalHeight,
        activePreset,
        framingMode,
      );
      setLoadedImage({
        element: image,
        name: file.name,
        type: file.type,
        size: file.size,
        width: image.naturalWidth,
        height: image.naturalHeight,
        zoom: placement.zoom,
        offsetX: placement.offsetX,
        offsetY: placement.offsetY,
      });
    } catch (error) {
      setLoadedImage(null);
      setErrorMessage(
        error instanceof Error
          ? error.message
          : 'This image could not be prepared in your browser.',
      );
    } finally {
      URL.revokeObjectURL(sourceUrl);
      setIsLoadingImage(false);
    }
  }

  async function handleWatermarkLogoSelection(file: File | null) {
    if (!file) {
      return;
    }

    markWorkflowCustom();
    setErrorMessage(null);
    setResultSize(null);

    const nextUrl = URL.createObjectURL(file);

    try {
      const element = await loadImageFromUrl(nextUrl);
      setWatermarkLogo((current) => {
        if (current?.url) {
          URL.revokeObjectURL(current.url);
        }

        return {
          element,
          url: nextUrl,
          name: file.name,
        };
      });
    } catch (error) {
      URL.revokeObjectURL(nextUrl);
      setErrorMessage(
        error instanceof Error
          ? error.message
          : 'The watermark image could not be prepared in your browser.',
      );
    }
  }

  function resetFraming() {
    if (!loadedImage) {
      return;
    }

    const placement = getDefaultPlacement(
      loadedImage.width,
      loadedImage.height,
      activePreset,
      framingMode,
    );
    setLoadedImage((current) =>
      current
        ? {
            ...current,
            zoom: placement.zoom,
            offsetX: placement.offsetX,
            offsetY: placement.offsetY,
          }
        : current,
    );
    resetOutputState();
  }

  function handlePointerDown(event: ReactPointerEvent<HTMLCanvasElement>) {
    if (!loadedImage) {
      return;
    }

    pointerDragRef.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      offsetX: loadedImage.offsetX,
      offsetY: loadedImage.offsetY,
    };
    setIsPointerDragging(true);
    event.currentTarget.setPointerCapture(event.pointerId);
  }

  function handlePointerMove(event: ReactPointerEvent<HTMLCanvasElement>) {
    if (
      !loadedImage ||
      !pointerDragRef.current ||
      pointerDragRef.current.pointerId !== event.pointerId
    ) {
      return;
    }

    const rect = event.currentTarget.getBoundingClientRect();
    const scaleX = activePreset.width / rect.width;
    const scaleY = activePreset.height / rect.height;
    const nextOffsets = clampOffsets(
      loadedImage.width,
      loadedImage.height,
      activePreset.width,
      activePreset.height,
      framingMode,
      loadedImage.zoom,
      pointerDragRef.current.offsetX + (event.clientX - pointerDragRef.current.startX) * scaleX,
      pointerDragRef.current.offsetY + (event.clientY - pointerDragRef.current.startY) * scaleY,
    );

    setLoadedImage((current) =>
      current
        ? {
            ...current,
            offsetX: nextOffsets.x,
            offsetY: nextOffsets.y,
          }
        : current,
    );
  }

  function handlePointerUp(event: ReactPointerEvent<HTMLCanvasElement>) {
    if (!pointerDragRef.current || pointerDragRef.current.pointerId !== event.pointerId) {
      return;
    }

    pointerDragRef.current = null;
    setIsPointerDragging(false);

    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
  }

  function handleZoomChange(nextZoom: number) {
    if (!loadedImage) {
      return;
    }

    markWorkflowCustom();
    const minimumZoom = framingMode === 'crop' ? 1 : 0.92;
    const clampedZoom = clamp(nextZoom, minimumZoom, 2.5);
    const nextOffsets = clampOffsets(
      loadedImage.width,
      loadedImage.height,
      activePreset.width,
      activePreset.height,
      framingMode,
      clampedZoom,
      loadedImage.offsetX,
      loadedImage.offsetY,
    );

    setLoadedImage((current) =>
      current
        ? {
            ...current,
            zoom: clampedZoom,
            offsetX: nextOffsets.x,
            offsetY: nextOffsets.y,
          }
        : current,
    );
    resetOutputState();
  }

  async function runExport() {
    if (!loadedImage) {
      setErrorMessage('Upload one product image first.');
      return;
    }

    setIsExporting(true);
    setErrorMessage(null);
    setCreditMessage(null);
    setResultSize(null);

    try {
      const headers: HeadersInit = {
        'Content-Type': 'application/json',
      };
      const idToken = await getToolAuthToken();

      if (idToken) {
        headers.authorization = `Bearer ${idToken}`;
      }

      const usageResponse = await fetch('/api/tools/usage', {
        method: 'POST',
        headers,
        body: JSON.stringify({ toolSlug: variant?.toolSlug ?? 'product-image-prep' }),
      });
      const usagePayload = (await usageResponse.json().catch(() => ({}))) as {
        success?: boolean;
        data?: {
          credits?: {
            plan?: 'guest' | 'free' | 'pro';
            remaining?: number | null;
            windowLabel?: string;
          };
        };
        error?: unknown;
      };

      if (!usageResponse.ok || !usagePayload.success) {
        const message =
          typeof usagePayload.error === 'object' &&
          usagePayload.error &&
          'message' in usagePayload.error
            ? String((usagePayload.error as { message?: unknown }).message)
            : typeof usagePayload.error === 'string'
              ? usagePayload.error
              : 'Credits are not available for another export right now.';
        throw new Error(message);
      }

      const canvas = document.createElement('canvas');
      canvas.width = activePreset.width;
      canvas.height = activePreset.height;
      const context = canvas.getContext('2d');

      if (!context) {
        throw new Error('Your browser could not prepare the export.');
      }

      drawPreparedImage(
        context,
        loadedImage,
        activePreset,
        activePreset.width,
        activePreset.height,
        framingMode,
        activeBackground.color,
        outputFormat,
        watermarkEnabled,
        watermarkKind,
        watermarkText,
        watermarkLogo,
        watermarkScale,
        watermarkOpacity,
        watermarkPadding,
        watermarkPosition,
      );

      const blob = await new Promise<Blob | null>((resolve) => {
        canvas.toBlob(
          (nextBlob) => resolve(nextBlob),
          selectedOutput.mimeType,
          selectedOutput.qualityEnabled ? quality : undefined,
        );
      });

      if (!blob) {
        throw new Error('The listing export could not finish for this image.');
      }

      const fileName = `${getBaseName(loadedImage.name)}-${activePreset.id}.${selectedOutput.extension}`;
      const downloadUrl = URL.createObjectURL(blob);
      triggerDownload(downloadUrl, fileName);
      setResultSize(blob.size);
      window.setTimeout(() => URL.revokeObjectURL(downloadUrl), 4000);

      setCreditMessage(
        usagePayload.data?.credits?.plan === 'pro'
          ? 'Local export finished. Metadata stripped on save and unlimited plan access is active.'
          : usagePayload.data?.credits?.remaining !== undefined
            ? `Local export finished. Metadata stripped on save. ${usagePayload.data.credits.remaining} credits left in this ${usagePayload.data.credits.windowLabel?.toLowerCase() || 'current'} window.`
            : 'Local export finished. Metadata stripped on save.',
      );
    } catch (error) {
      setErrorMessage(
        error instanceof Error ? error.message : 'The listing-ready export could not finish.',
      );
    } finally {
      setIsExporting(false);
    }
  }

  return (
    <article className="tool-live-card">
      <div className="tool-live-header">
        <div>
          <h2>Prepare listing-ready product images in the browser</h2>
          <p>
            Upload one image, pick a merchant preset, adjust the framing if needed, and export a
            cleaner marketplace-ready file with local browser processing.
          </p>
        </div>
        <span className="tool-live-badge">{variant?.badgeLabel ?? 'Local browser processing'}</span>
      </div>

      <div className="tool-product-prep-steps" aria-label="Workflow steps">
        <div className="tool-product-prep-step">
          <strong>1. Upload</strong>
          <span>Choose one product image</span>
        </div>
        <div className="tool-product-prep-step">
          <strong>2. Prepare</strong>
          <span>Preset, framing, background</span>
        </div>
        <div className="tool-product-prep-step">
          <strong>3. Export</strong>
          <span>Download a ready-to-upload file</span>
        </div>
      </div>

      <div className="tool-live-grid">
        <div className="tool-live-uploader">
          <div className="tool-product-prep-section">
            <div className="tool-product-prep-section-heading">
              <strong>Upload</strong>
              <span>One clear merchant path: upload, prepare, export.</span>
            </div>
            <button
              className={`tool-live-dropzone ${isDragActive ? 'tool-live-dropzone-active' : ''}`}
              onClick={() => inputRef.current?.click()}
              onDragEnter={() => setIsDragActive(true)}
              onDragLeave={() => setIsDragActive(false)}
              onDragOver={(event) => {
                event.preventDefault();
                setIsDragActive(true);
              }}
              onDrop={(event) => {
                event.preventDefault();
                setIsDragActive(false);
                void handleFileSelection(event.dataTransfer.files?.[0] ?? null);
              }}
              type="button"
            >
              <div>
                <strong>
                  {isLoadingImage
                    ? 'Preparing image locally…'
                    : loadedImage
                      ? loadedImage.name
                      : 'Upload product image or drop it here'}
                </strong>
                <span>
                  {loadedImage
                    ? `${formatFileSize(loadedImage.size)} • ${normalizeSourceLabel(loadedImage.type)} • ${loadedImage.width} × ${loadedImage.height}`
                    : 'PNG, JPG, JPEG, WebP, GIF, or BMP up to 12 MB. Batch export can come later; this flow stays focused on one product image.'}
                </span>
              </div>
            </button>
            <input
              ref={inputRef}
              accept="image/*"
              className="tool-live-file-input"
              onChange={(event) => void handleFileSelection(event.target.files?.[0] ?? null)}
              type="file"
            />
          </div>

          <div className="tool-product-prep-section">
            <div className="tool-product-prep-section-heading">
              <strong>Prepare</strong>
              <span>Start with a quick preset, then only touch what needs adjusting.</span>
            </div>

            <div className="tool-live-routes" aria-label="Merchant quick presets">
              {workflowPresets.map((workflow) => (
                <button
                  key={workflow.id}
                  className={`tool-live-route ${activeWorkflowId === workflow.id ? 'tool-live-route-active' : ''}`}
                  onClick={() => applyWorkflowPreset(workflow)}
                  type="button"
                >
                  {workflow.label}
                </button>
              ))}
            </div>

            <div className="tool-live-preview tool-editor-preview tool-product-prep-shell">
              <div className="tool-image-editor-header">
                <h3>Preview</h3>
                <div className="tool-image-editor-header-actions">
                  <button
                    className="button button-ghost tool-image-editor-revert"
                    disabled={!loadedImage}
                    onClick={resetFraming}
                    type="button"
                  >
                    <span>Reset framing</span>
                  </button>
                </div>
              </div>
              <div
                ref={previewFrameRef}
                className="tool-live-preview-frame tool-editor-preview-frame tool-product-prep-frame"
              >
                {loadedImage ? (
                  <div
                    className="tool-product-prep-stage"
                    aria-label={`${activePreset.label} preview`}
                    style={{
                      aspectRatio: `${previewSize.width} / ${previewSize.height}`,
                      width: `${previewSize.width}px`,
                      height: `${previewSize.height}px`,
                    }}
                  >
                    <canvas
                      ref={previewCanvasRef}
                      className={`tool-editor-canvas tool-product-prep-canvas ${isPointerDragging ? 'tool-product-prep-canvas-dragging' : ''}`}
                      onPointerCancel={handlePointerUp}
                      onPointerDown={handlePointerDown}
                      onPointerMove={handlePointerMove}
                      onPointerUp={handlePointerUp}
                    />
                    <div className="tool-product-prep-guides" aria-hidden="true">
                      <div
                        className="tool-product-prep-guide-safe-area"
                        style={{ inset: safeInsetLabel }}
                      />
                      <div className="tool-product-prep-guide-line" />
                    </div>
                  </div>
                ) : (
                  <div className="tool-editor-empty tool-product-prep-empty">
                    <strong>Preview will appear here</strong>
                    <span>
                      Upload one product image, then drag inside the frame only if the default crop
                      needs a small adjustment.
                    </span>
                    <small>Canvas background is a listing fill, not true background removal.</small>
                  </div>
                )}
              </div>
            </div>

            <div className="tool-live-controls">
              {variant?.hidePresetChooser ? null : (
                <label className="field">
                  <span>Preset</span>
                  <select
                    onChange={(event) => {
                      markWorkflowCustom();
                      setActivePresetId(event.target.value);
                      resetOutputState();
                    }}
                    value={activePresetId}
                  >
                    {visiblePresets.map((preset) => (
                      <option key={preset.id} value={preset.id}>
                        {`${preset.label} • ${preset.width} × ${preset.height}`}
                      </option>
                    ))}
                  </select>
                </label>
              )}
              <label className="field">
                <span>Framing</span>
                <select
                  onChange={(event) => {
                    markWorkflowCustom();
                    setFramingMode(event.target.value as FramingMode);
                    resetOutputState();
                    if (loadedImage) {
                      const placement = getDefaultPlacement(
                        loadedImage.width,
                        loadedImage.height,
                        activePreset,
                        event.target.value as FramingMode,
                      );
                      setLoadedImage({
                        ...loadedImage,
                        zoom: placement.zoom,
                        offsetX: placement.offsetX,
                        offsetY: placement.offsetY,
                      });
                    }
                  }}
                  value={framingMode}
                >
                  {framingModes.map((mode) => (
                    <option key={mode.value} value={mode.value}>
                      {mode.label}
                    </option>
                  ))}
                </select>
              </label>
              <label className="field">
                <span>Background</span>
                <select
                  onChange={(event) => {
                    markWorkflowCustom();
                    setBackgroundPreset(event.target.value as BackgroundPreset);
                    resetOutputState();
                  }}
                  value={backgroundPreset}
                >
                  {backgroundOptions.map((background) => (
                    <option key={background.value} value={background.value}>
                      {background.label}
                    </option>
                  ))}
                </select>
              </label>
              <label className="field tool-live-quality">
                <span>Scale</span>
                <input
                  disabled={!loadedImage}
                  max="2.5"
                  min={framingMode === 'crop' ? '1' : '0.92'}
                  onChange={(event) => handleZoomChange(Number(event.target.value))}
                  step="0.01"
                  type="range"
                  value={loadedImage?.zoom ?? 1}
                />
                <small>{loadedImage ? `${loadedImage.zoom.toFixed(2)}×` : 'Upload first'}</small>
              </label>
            </div>

            <div className="tool-product-prep-advanced">
              <button
                className="button button-ghost tool-product-prep-advanced-toggle"
                onClick={() => setIsAdvancedOpen((current) => !current)}
                type="button"
              >
                {isAdvancedOpen ? 'Hide advanced' : 'Show advanced'}
              </button>

              {isAdvancedOpen ? (
                <div className="tool-product-prep-advanced-panel">
                  <div className="tool-live-controls">
                    <label className="field">
                      <span>Format</span>
                      <select
                        onChange={(event) => {
                          markWorkflowCustom();
                          setOutputFormat(event.target.value as OutputFormat);
                          resetOutputState();
                        }}
                        value={outputFormat}
                      >
                        {outputOptions.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </label>
                    <label
                      className={`field tool-live-quality ${selectedOutput.qualityEnabled ? '' : 'tool-live-quality-disabled'}`}
                    >
                      <span>Compression</span>
                      <input
                        disabled={!selectedOutput.qualityEnabled}
                        max="0.98"
                        min="0.45"
                        onChange={(event) => {
                          markWorkflowCustom();
                          setQuality(Number(event.target.value));
                          resetOutputState();
                        }}
                        step="0.01"
                        type="range"
                        value={quality}
                      />
                      <small>
                        {selectedOutput.qualityEnabled
                          ? qualityProfile
                          : 'PNG keeps cleaner edges without a compression slider.'}
                      </small>
                    </label>
                    <label className="field">
                      <span>Watermark</span>
                      <select
                        onChange={(event) => {
                          markWorkflowCustom();
                          const enabled = event.target.value !== 'off';
                          setWatermarkEnabled(enabled);
                          if (enabled) {
                            setWatermarkKind(event.target.value as WatermarkKind);
                          }
                          resetOutputState();
                        }}
                        value={watermarkEnabled ? watermarkKind : 'off'}
                      >
                        <option value="off">Off</option>
                        <option value="text">Text</option>
                        <option value="logo">Logo</option>
                      </select>
                    </label>
                    <label className="field">
                      <span>Position</span>
                      <select
                        disabled={!watermarkEnabled}
                        onChange={(event) => {
                          markWorkflowCustom();
                          setWatermarkPosition(event.target.value as WatermarkPosition);
                          resetOutputState();
                        }}
                        value={watermarkPosition}
                      >
                        {watermarkPositions.map((position) => (
                          <option key={position.value} value={position.value}>
                            {position.label}
                          </option>
                        ))}
                      </select>
                    </label>
                  </div>

                  <div className="tool-live-controls tool-product-prep-watermark-controls">
                    <label className="field">
                      <span>{watermarkKind === 'logo' ? 'Logo file' : 'Text'}</span>
                      {watermarkEnabled && watermarkKind === 'logo' ? (
                        <>
                          <button
                            className="button button-ghost tool-product-prep-logo-picker"
                            onClick={() => logoInputRef.current?.click()}
                            type="button"
                          >
                            {watermarkLogo ? `Logo: ${watermarkLogo.name}` : 'Choose logo'}
                          </button>
                          <input
                            ref={logoInputRef}
                            accept="image/*"
                            className="tool-live-file-input"
                            onChange={(event) =>
                              void handleWatermarkLogoSelection(event.target.files?.[0] ?? null)
                            }
                            type="file"
                          />
                        </>
                      ) : (
                        <input
                          disabled={!watermarkEnabled}
                          onChange={(event) => {
                            markWorkflowCustom();
                            setWatermarkText(event.target.value);
                            resetOutputState();
                          }}
                          placeholder="Brand"
                          type="text"
                          value={watermarkText}
                        />
                      )}
                    </label>
                    <label className="field tool-live-quality">
                      <span>Opacity</span>
                      <input
                        disabled={!watermarkEnabled}
                        max="0.95"
                        min="0.2"
                        onChange={(event) => {
                          markWorkflowCustom();
                          setWatermarkOpacity(Number(event.target.value));
                          resetOutputState();
                        }}
                        step="0.01"
                        type="range"
                        value={watermarkOpacity}
                      />
                      <small>
                        {watermarkEnabled ? `${Math.round(watermarkOpacity * 100)}%` : 'Off'}
                      </small>
                    </label>
                    <label className="field tool-live-quality">
                      <span>Size</span>
                      <input
                        disabled={!watermarkEnabled}
                        max="0.3"
                        min="0.08"
                        onChange={(event) => {
                          markWorkflowCustom();
                          setWatermarkScale(Number(event.target.value));
                          resetOutputState();
                        }}
                        step="0.01"
                        type="range"
                        value={watermarkScale}
                      />
                      <small>
                        {watermarkEnabled ? `${Math.round(watermarkScale * 100)}%` : 'Off'}
                      </small>
                    </label>
                    <label className="field tool-live-quality">
                      <span>Padding</span>
                      <input
                        disabled={!watermarkEnabled}
                        max="0.12"
                        min="0.02"
                        onChange={(event) => {
                          markWorkflowCustom();
                          setWatermarkPadding(Number(event.target.value));
                          resetOutputState();
                        }}
                        step="0.01"
                        type="range"
                        value={watermarkPadding}
                      />
                      <small>
                        {watermarkEnabled ? `${Math.round(watermarkPadding * 100)}% inset` : 'Off'}
                      </small>
                    </label>
                  </div>
                </div>
              ) : null}
            </div>
          </div>

          <div className="tool-product-prep-section">
            <div className="tool-product-prep-section-heading">
              <strong>Export</strong>
              <span>Download the final listing file after the preview looks right.</span>
            </div>
            <div className="hero-actions tool-live-actions">
              <button
                className="button"
                disabled={!loadedImage || isExporting || isLoadingImage}
                onClick={() => void runExport()}
                type="button"
              >
                {!loadedImage
                  ? 'Upload image to export'
                  : isLoadingImage
                    ? 'Preparing image…'
                    : isExporting
                      ? 'Exporting locally…'
                      : `Export as ${selectedOutput.label}`}
              </button>
            </div>
          </div>

          {creditMessage ? (
            <p className="support-feedback support-feedback-success">{creditMessage}</p>
          ) : null}
          {errorMessage ? (
            <p className="support-feedback support-feedback-error">{errorMessage}</p>
          ) : null}
        </div>

        <aside className="tool-live-panel tool-product-prep-summary">
          <h3>Setup</h3>
          <div className="tool-live-stats">
            <div>
              <strong>Preset</strong>
              <span>{activePreset.label}</span>
            </div>
            <div>
              <strong>Format</strong>
              <span>{selectedOutput.label}</span>
            </div>
            <div>
              <strong>Export size</strong>
              <span>{`${activePreset.width} × ${activePreset.height}`}</span>
            </div>
            <div>
              <strong>Watermark</strong>
              <span>{watermarkEnabled ? 'On' : 'Off'}</span>
            </div>
            <div>
              <strong>Est. weight</strong>
              <span>{resultSize ? formatFileSize(resultSize) : estimatedOutputWeight}</span>
            </div>
          </div>
          <div className="tool-live-panel-note">
            <strong>Local processing</strong>
            <span>
              Preview and export stay in the browser, and the saved file is rebuilt from canvas so
              common metadata is stripped on save.
            </span>
          </div>
        </aside>
      </div>
    </article>
  );
}
